export type ApprovalDecision = 'approve' | 'reject';

/**
 * Resolves a pending write action by calling the MCP approval endpoint directly with the human's
 * own token. This is a plain HTTP route (not an MCP tool), so the assistant's LLM cannot reach it —
 * only the human's browser can. MCP re-validates that the approver is the original requester,
 * re-authorizes the stored args, and atomically claims the action before executing.
 *
 * MCP is fronted by API Gateway (not CloudFront OAC), so the JWT travels in the standard
 * Authorization header and no request-body signing is needed.
 */
export const resolveAction = async (
  mcpUrl: string,
  token: string,
  actionId: string,
  decision: ApprovalDecision
): Promise<string> => {
  const base = mcpUrl.replace(/\/+$/, '');
  const response = await fetch(`${base}/mcp/actions/${encodeURIComponent(actionId)}/${decision}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });

  const body = (await response.json().catch(() => ({}))) as { error?: string; status?: string };
  if (!response.ok) {
    throw new Error(body.error ?? `Failed to ${decision} action (HTTP ${response.status}).`);
  }
  return body.status ?? (decision === 'approve' ? 'EXECUTED' : 'REJECTED');
};
